import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Alert,
  Box,
  InputAdornment,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LocalTaxiIcon from "@mui/icons-material/LocalTaxi";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";

import {
  IPD_COLORS,
  IPD_SHAPE,
  ipdCardSx,
  ipdPageSx,
  ipdElevatedInputSx,
  IpdButton,
  IpdPageHeader,
  IpdTabBar,
} from "../../../ipd/utils";
import {
  MlcBadge,
  EmergencyPlaceholder,
  StageBadge,
  TriageBadge,
  TriageStripe,
} from "../../components/EmergencyBadges";
import { ARRIVAL_MODE_LABEL, formatDateTime, visitStage } from "../../constants";
import { listEmergencyVisits } from "../../api/emergencyApi";

/**
 * Every emergency visit, not just the ones on the board — to find a patient who
 * has already left, check an outcome, or pull up someone registered earlier in
 * the shift (ER: All-Visits).
 *
 * Open and closed sit under separate tabs; search runs on the server (the
 * visit list's `search` param), because closed visits accumulate without bound
 * and the one being looked for may be older than the row limit — filtering the
 * page on the client would only ever search what happened to be loaded.
 */

const TABS = [
  { id: "open", label: "Open" },
  { id: "closed", label: "Closed" },
];

const OUTCOME_LABEL = {
  discharged: "Discharged",
  admitted: "Admitted",
  transferred: "Transferred",
  expired: "Expired",
  lwbs: "Left without being seen",
  ama: "Against medical advice",
};

function OutcomeChip({ disposition }) {
  if (!disposition) return null;
  return (
    <Box
      component="span"
      sx={{
        px: 1,
        py: 0.35,
        borderRadius: `${IPD_SHAPE.radiusMd}px`,
        fontSize: 11.5,
        fontWeight: 700,
        color: IPD_COLORS.textSecondary,
        bgcolor: IPD_COLORS.pageBg,
        border: `1px solid ${IPD_COLORS.border}`,
        whiteSpace: "nowrap",
      }}
    >
      {OUTCOME_LABEL[disposition.outcome] || disposition.outcome}
    </Box>
  );
}

function VisitRow({ visit, onOpen }) {
  const unnamed = !visit.patient_name;
  const closed = visit.status === "closed";
  const ArrivalIcon = visit.arrival_mode === "ambulance" ? LocalTaxiIcon : DirectionsWalkIcon;

  return (
    <Box
      role="link"
      tabIndex={0}
      aria-label={`Open ${visit.display_name}'s visit`}
      onClick={() => onOpen(visit)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(visit);
        }
      }}
      sx={{
        ...ipdCardSx,
        position: "relative",
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.8fr) 116px minmax(120px, 1fr) auto" },
        alignItems: "center",
        gap: { xs: 1, md: 2 },
        p: 2,
        pl: 3,
        cursor: "pointer",
        transition: "box-shadow .2s ease, border-color .2s ease",
        "&:hover": {
          borderColor: IPD_COLORS.borderHover,
          boxShadow: IPD_COLORS.shadow.cardHover,
        },
        "&:focus-visible": {
          outline: `2px solid ${IPD_COLORS.primary}`,
          outlineOffset: 2,
        },
      }}
    >
      <TriageStripe category={visit.triage_category} />

      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 14,
              color: IPD_COLORS.textPrimary,
              fontStyle: unnamed ? "italic" : "normal",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {visit.display_name}
          </Typography>
          {visit.is_mlc && <MlcBadge caseNumber={visit.mlc_case_number} compact />}
          <Tooltip title={ARRIVAL_MODE_LABEL[visit.arrival_mode] || "Arrival mode not recorded"}>
            <ArrivalIcon sx={{ fontSize: 15, color: IPD_COLORS.textMuted }} />
          </Tooltip>
        </Box>
        <Typography
          sx={{
            mt: 0.25,
            fontSize: 12,
            color: IPD_COLORS.textSecondary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {visit.visit_number}
          {visit.patient_id ? ` · ${visit.patient_id}` : ""}
          {visit.age ? ` · ${visit.age}y` : ""}
          {visit.chief_complaint ? ` · ${visit.chief_complaint}` : ""}
        </Typography>
      </Box>

      <Box>
        <TriageBadge category={visit.triage_category} />
      </Box>

      <Box sx={{ minWidth: 0 }}>
        {closed ? (
          <OutcomeChip disposition={visit.disposition} />
        ) : (
          <StageBadge stage={visitStage(visit)} />
        )}
      </Box>

      <Typography
        sx={{
          fontSize: 12,
          color: IPD_COLORS.textMuted,
          fontVariantNumeric: "tabular-nums",
          textAlign: { md: "right" },
          whiteSpace: "nowrap",
        }}
      >
        {closed && visit.closed_at
          ? `Closed ${formatDateTime(visit.closed_at)}`
          : `Arrived ${formatDateTime(visit.arrived_at)}`}
      </Typography>
    </Box>
  );
}

export default function AllVisits() {
  const history = useHistory();
  const [tab, setTab] = useState("open");
  const [rawSearch, setRawSearch] = useState("");
  const [search, setSearch] = useState("");
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Only the newest request may write state. A tab switch is not debounced, so
  // an earlier tab's slower response can land after a later one; without this
  // guard it would overwrite the current tab's rows with stale ones.
  const reqIdRef = useRef(0);

  // Debounce so a search hits the server once the typing settles, not per key.
  useEffect(() => {
    const t = setTimeout(() => setSearch(rawSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [rawSearch]);

  const load = useCallback(async () => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    try {
      const { visits: rows } = await listEmergencyVisits({ status: tab, search });
      if (reqIdRef.current !== reqId) return;
      setVisits(rows);
      setError("");
    } catch (e) {
      if (reqIdRef.current !== reqId) return;
      setVisits([]);
      setError(e?.message || "Could not load the visits");
    } finally {
      if (reqIdRef.current === reqId) setLoading(false);
    }
  }, [tab, search]);

  useEffect(() => {
    load();
  }, [load]);

  const open = (visit) => history.push(`/emergency_visit/${visit.id}`);

  return (
    <Box sx={{ ...ipdPageSx, p: { xs: 2, md: 3 } }}>
      <IpdPageHeader
        eyebrow="Emergency Department"
        title="All Visits"
        description="Every emergency visit — open on the board now, or already closed."
        actions={
          <IpdButton
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push("/emergency_tracking_board")}
          >
            Board
          </IpdButton>
        }
      />

      <Box sx={{ mt: 1 }}>
        <IpdTabBar
          tabs={TABS}
          activeTab={tab}
          onChange={setTab}
          ariaLabel="Open or closed visits"
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 2.5, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          value={rawSearch}
          onChange={(e) => setRawSearch(e.target.value)}
          placeholder="Search name, temporary label, visit number or hospital ID…"
          size="small"
          sx={{ ...ipdElevatedInputSx, flex: 1, minWidth: 260, maxWidth: 460 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: IPD_COLORS.textMuted }} />
              </InputAdornment>
            ),
          }}
          inputProps={{ "aria-label": "Search all emergency visits" }}
        />
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}
          action={
            <IpdButton variant="outlined" size="small" onClick={load}>
              Retry
            </IpdButton>
          }
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton
              key={i}
              variant="rounded"
              height={64}
              sx={{ borderRadius: `${IPD_SHAPE.radiusMd}px` }}
            />
          ))}
        </Box>
      ) : visits.length === 0 ? (
        <EmergencyPlaceholder
          icon={PeopleAltIcon}
          title={
            search
              ? "No matching visits"
              : tab === "open"
              ? "No open visits"
              : "No closed visits"
          }
          description={
            search
              ? "Try a different name, visit number or hospital identifier."
              : tab === "open"
              ? "Open visits appear here the moment a patient is registered."
              : "Closed visits appear here once a visit reaches its disposition."
          }
        />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {visits.map((v) => (
            <VisitRow key={v.id} visit={v} onOpen={open} />
          ))}
        </Box>
      )}
    </Box>
  );
}
